// components/features/modules/ModuleForm.tsx

import { useState } from "react";
import { BaseModule, Module, Endpoint } from "../../../types/features/Module";
import { 
  Phone, Ticket, MessageSquare, Mail, 
  Database, BarChart3,
  Globe, Lock, Bell, FileText,
  Clock, Calendar, Heart, Award, MapPin,
  Cloud, Battery, Camera, Music,
  Book, Code, Compass, Crown, Diamond, Eye,
  Gift, Home, Key, Layers, Lightbulb, Link,
  List, Loader, LogOut, Menu, Moon, MoreHorizontal, Navigation, Package, Paperclip, Pause,
  PieChart, Play, Plus, Power, Printer, Radio,
  RefreshCw, Repeat, Rewind, Rocket, Save, Search,
  Send, Server, Settings, Share, Shield, ShoppingBag,
  Shuffle, Sliders, Smartphone, Smile, Speaker,
  Star, Sun, Sunset, Tablet, Tag, Target,
  Terminal, ThumbsUp, ThumbsDown, Timer,
  Trash2, TrendingUp, Trophy, Truck, Tv, Twitch,
  Twitter, Umbrella, Unlock, Upload, User, UserPlus,
  Users, Video, Voicemail, Volume2, Wallet, Watch,
  Wifi, Wind, Wrench, X, Zap, ZoomIn,
  LucideIcon, PlusCircle
} from "lucide-react";

interface ModuleFormProps {
  initialData?: Partial<Module>;
  onSubmit: (data: BaseModule) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isEdit?: boolean;
}

// Liste des icônes disponibles (nom affiché + composant)
const AVAILABLE_ICONS = [
  { name: "Phone", icon: Phone },
  { name: "Ticket", icon: Ticket },
  { name: "MessageSquare", icon: MessageSquare },
  { name: "Mail", icon: Mail },
  { name: "Smartphone", icon: Smartphone },
  { name: "Users", icon: Users },
  { name: "User", icon: User },
  { name: "Database", icon: Database },
  { name: "BarChart3", icon: BarChart3 },
  { name: "Settings", icon: Settings },
  { name: "Zap", icon: Zap },
  { name: "Globe", icon: Globe },
  { name: "Lock", icon: Lock },
  { name: "Shield", icon: Shield },
  { name: "Bell", icon: Bell },
  { name: "Video", icon: Video },
  { name: "FileText", icon: FileText },
  { name: "Clock", icon: Clock },
  { name: "Calendar", icon: Calendar },
  { name: "Star", icon: Star },
  { name: "Heart", icon: Heart },
  { name: "Award", icon: Award },
  { name: "MapPin", icon: MapPin },
  { name: "Cloud", icon: Cloud },
  { name: "Wifi", icon: Wifi },
  { name: "Camera", icon: Camera },
  { name: "Book", icon: Book },
  { name: "Code", icon: Code },
  { name: "Compass", icon: Compass },
  { name: "Crown", icon: Crown },
  { name: "Diamond", icon: Diamond },
  { name: "Eye", icon: Eye },
  { name: "Gift", icon: Gift },
  { name: "Home", icon: Home },
  { name: "Key", icon: Key },
  { name: "Layers", icon: Layers },
  { name: "Lightbulb", icon: Lightbulb },
  { name: "Link", icon: Link },
  { name: "Package", icon: Package },
  { name: "PieChart", icon: PieChart },
  { name: "Rocket", icon: Rocket },
  { name: "Save", icon: Save },
  { name: "Send", icon: Send },
  { name: "Server", icon: Server },
  { name: "Share", icon: Share },
  { name: "ShoppingBag", icon: ShoppingBag },
  { name: "Sliders", icon: Sliders },
  { name: "Smile", icon: Smile },
  { name: "Speaker", icon: Speaker },
  { name: "Tag", icon: Tag },
  { name: "Target", icon: Target },
  { name: "Trash2", icon: Trash2 },
  { name: "TrendingUp", icon: TrendingUp },
  { name: "Trophy", icon: Trophy },
  { name: "Truck", icon: Truck },
  { name: "Wallet", icon: Wallet },
  { name: "Watch", icon: Watch },
  { name: "X", icon: X },
  { name: "ZoomIn", icon: ZoomIn },
];

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

// Générer une clé à partir du nom
const generateKey = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_');
};

// Vérifier si une icône existe dans la liste
const isIconAvailable = (iconName: string): boolean => {
  return AVAILABLE_ICONS.some(i => i.name === iconName);
};

export default function ModuleForm({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  isEdit = false
}: ModuleFormProps) {
  const [formData, setFormData] = useState({
    name: initialData.name || "",
    key: initialData.key || "",
    icon: initialData.icon || "Package",
    description: initialData.description || "",
    endpoints: initialData.endpoints || [],
    is_active: initialData.is_active ?? true,
  });

  const [isCustomIcon, setIsCustomIcon] = useState(
    initialData.icon ? !isIconAvailable(initialData.icon) : false
  );

  const [newEndpoint, setNewEndpoint] = useState<Endpoint>({
    method: "GET",
    description: "",
    link: "",
  });

  // Mettre à jour la clé automatiquement quand le nom change
  const handleNameChange = (name: string) => {
    const generatedKey = generateKey(name);
    setFormData(prev => ({
      ...prev,
      name,
      key: prev.key === generateKey(prev.name) || !prev.key ? generatedKey : prev.key,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      ...formData,
      key: formData.key || generateKey(formData.name) ,
    };
    onSubmit(data);
  };

  const addEndpoint = () => {
    if (!newEndpoint.link.trim() || !newEndpoint.description.trim()) return;
    setFormData(prev => ({
      ...prev,
      endpoints: [...(prev.endpoints || []), { ...newEndpoint }],
    }));
    setNewEndpoint({ method: "GET", description: "", link: "" });
  };

  const removeEndpoint = (index: number) => {
    setFormData(prev => ({
      ...prev,
      endpoints: (prev.endpoints || []).filter((_, i) => i !== index),
    }));
  };

  // Récupérer l'icône sélectionnée
  const getSelectedIcon = () => {
    if (isCustomIcon) {
      return Package;
    }
    return AVAILABLE_ICONS.find(i => i.name === formData.icon)?.icon || Package;
  };

  const SelectedIcon = getSelectedIcon();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Nom */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nom du module *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleNameChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
          placeholder="Ex: Tickets, Appels, WhatsApp..."
          required
        />
        <p className="text-xs text-gray-400 mt-1">
          La clé sera générée automatiquement à partir du nom
        </p>
      </div>

      {/* Clé */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Clé du module *
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={formData.key}
            onChange={(e) => setFormData({ ...formData, key: e.target.value })}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none font-mono text-sm"
            placeholder="ex: tickets, appels, whatsapp..."
            required
            disabled={isEdit}
          />
          <button
            type="button"
            onClick={() => setFormData({ 
              ...formData, 
              key: generateKey(formData.name) 
            })}
            className="px-3 py-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
            disabled={isEdit}
          >
            Générer
          </button>
        </div>
        {isEdit && (
          <p className="text-xs text-amber-600 mt-1">⚠️ La clé ne peut pas être modifiée</p>
        )}
        {!isEdit && (
          <p className="text-xs text-gray-400 mt-1">
            La clé est utilisée dans le code, modifiez-la avec précaution
          </p>
        )}
      </div>

      {/* Icône */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Icône *
        </label>
        
        <div className="flex items-center gap-3 mb-2">
          <button
            type="button"
            onClick={() => setIsCustomIcon(false)}
            className={`text-xs px-3 py-1 rounded-full transition-colors ${
              !isCustomIcon 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Choisir dans la liste
          </button>
          <button
            type="button"
            onClick={() => setIsCustomIcon(true)}
            className={`text-xs px-3 py-1 rounded-full transition-colors ${
              isCustomIcon 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Icône personnalisée
          </button>
        </div>

        {!isCustomIcon ? (
          <div className="flex gap-2">
            <div className="flex-1">
              <select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                required
              >
                {AVAILABLE_ICONS.map((icon) => (
                  <option key={icon.name} value={icon.name}>
                    {icon.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center shrink-0">
              <SelectedIcon size={20} className="text-gray-600" />
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none font-mono text-sm"
                placeholder="Ex: CustomIcon, MyIcon..."
                required
              />
            </div>
            <div className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center shrink-0 bg-gray-50">
              <span className="text-xs text-gray-400 font-mono">
                {formData.icon ? formData.icon.substring(0, 2).toUpperCase() : "?"}
              </span>
            </div>
          </div>
        )}
        
        {isCustomIcon ? (
          <p className="text-xs text-amber-600 mt-1">
            ⚠️ Assurez-vous que l'icône existe dans lucide-react
          </p>
        ) : (
          <p className="text-xs text-gray-400 mt-1">
            Sélectionnez une icône dans la liste ou passez en mode personnalisé
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none resize-none"
          placeholder="Description du module..."
          rows={3}
        />
      </div>

      {/* Endpoints */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Endpoints
        </label>
        <div className="space-y-2">
          {/* Liste des endpoints existants */}
          {(formData.endpoints || []).map((endpoint: Endpoint, index: number) => (
            <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
              <span className="text-xs font-mono font-bold px-2 py-1 rounded bg-primary/10 text-primary">
                {endpoint.method}
              </span>
              <span className="text-xs text-gray-600 flex-1">{endpoint.description}</span>
              <span className="text-xs font-mono text-gray-400">{endpoint.link}</span>
              <button
                type="button"
                onClick={() => removeEndpoint(index)}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}

          {/* Ajouter un endpoint */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <select
              value={newEndpoint.method}
              onChange={(e) => setNewEndpoint({ ...newEndpoint, method: e.target.value as any })}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
            >
              {HTTP_METHODS.map((method) => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
            <input
              type="text"
              value={newEndpoint.description}
              onChange={(e) => setNewEndpoint({ ...newEndpoint, description: e.target.value })}
              placeholder="Description"
              className="flex-1 min-w-[120px] px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
            />
            <input
              type="text"
              value={newEndpoint.link}
              onChange={(e) => setNewEndpoint({ ...newEndpoint, link: e.target.value })}
              placeholder="/api/endpoint"
              className="flex-1 min-w-[120px] px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none font-mono"
            />
            <button
              type="button"
              onClick={addEndpoint}
              className="px-2 py-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors"
            >
              <PlusCircle size={18} />
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Ajoutez les endpoints disponibles pour ce module
        </p>
      </div>

      {/* Statut */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Statut</label>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            formData.is_active ? 'bg-green-500' : 'bg-gray-300'
          }`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            formData.is_active ? 'translate-x-6' : ''
          }`} />
        </button>
        <span className="text-sm text-gray-500">
          {formData.is_active ? 'Actif' : 'Inactif'}
        </span>
      </div>

      {/* Boutons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Chargement...' : isEdit ? 'Mettre à jour' : 'Créer'}
        </button>
      </div>
    </form>
  );
}